import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  Gig,
  PaymentConnection,
  PaymentConnectionStatus,
  Tip,
  TipStatus,
  ExternalTipLink,
} from '@stage/contracts';
import { ApiError } from '../api/client.js';
import type { TipSummary } from '../api/paymentsClient.js';
import { useAdminClient } from '../hooks/useAdminClient.js';
import { usePaymentsClient } from '../hooks/usePaymentsClient.js';
import { dollarsToMinor, formatMinor } from '../lib/money.js';

/**
 * Tipping management (M6, §22): Stripe Connect onboarding, the per-gig tip-config editor (presets +
 * external tip links), and the artist tip report. Copy never implies a tip buys request priority
 * (§22.1), native tipping stays off until the connection is `ready` (§22.3 step 3), and
 * missing/pending report data is rendered as "—"/"pending", never zero (§22.8).
 */

const CONNECTION_LABEL: Record<PaymentConnectionStatus, string> = {
  disconnected: 'Not connected',
  onboarding: 'Onboarding in progress',
  ready: 'Ready',
  restricted: 'Restricted',
};

const TIP_LINK_METHODS: ExternalTipLink['method'][] = [
  'paypal',
  'venmo',
  'cashapp',
  'interac',
  'custom',
];

interface LinkDraft {
  method: ExternalTipLink['method'];
  label: string;
  url: string;
  instructions: string;
  enabled: boolean;
}

function toDraft(link: ExternalTipLink): LinkDraft {
  return {
    method: link.method,
    label: link.label,
    url: link.url ?? '',
    instructions: link.instructions ?? '',
    enabled: link.enabled,
  };
}

function emptyDraft(): LinkDraft {
  return { method: 'paypal', label: '', url: '', instructions: '', enabled: true };
}

/** Format an ISO timestamp compactly, tolerating a missing value (§22.8: never fabricate). */
function fmtTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function TippingPage() {
  const { gigId = '' } = useParams();
  const admin = useAdminClient();
  const payments = usePaymentsClient();

  const [gig, setGig] = useState<Gig | null>(null);
  const [connection, setConnection] = useState<PaymentConnection | null>(null);
  const [tips, setTips] = useState<Tip[]>([]);
  const [summary, setSummary] = useState<TipSummary | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Tip-config editable state.
  const [heading, setHeading] = useState('');
  const [thankYouText, setThankYouText] = useState('');
  const [allowCustom, setAllowCustom] = useState(true);
  const [currency, setCurrency] = useState('USD');
  const [presetDollars, setPresetDollars] = useState<string[]>([]);
  const [links, setLinks] = useState<LinkDraft[]>([]);

  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<ApiError | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const g = await admin.getGig(gigId);
      setGig(g);
      const [config, tipsResult, conn] = await Promise.all([
        payments.getTipConfig(gigId),
        payments.getTips(gigId),
        g.artistId
          ? payments.getPaymentConnection(g.artistId)
          : Promise.resolve<PaymentConnection | null>(null),
      ]);
      setConnection(conn);
      setTips(tipsResult.items);
      setSummary(tipsResult.summary);
      setHeading(config.policy.heading ?? '');
      setThankYouText(config.policy.thankYouText ?? '');
      setAllowCustom(config.policy.allowCustom);
      setPresetDollars(config.policy.presets.map((m) => formatMinor(m.amountMinor)));
      const firstPreset = config.policy.presets[0];
      setCurrency(firstPreset?.currency ?? conn?.currencies[0] ?? 'USD');
      setLinks(config.externalLinks.map(toDraft));
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Could not load tipping settings.');
    }
  }, [admin, payments, gigId]);

  useEffect(() => {
    void load();
  }, [load]);

  const connReady = connection?.status === 'ready';

  const startOnboarding = useCallback(async () => {
    if (!gig?.artistId) return;
    setBusy('onboarding');
    setNotice(null);
    try {
      const here = window.location.href;
      const { url } = await payments.createOnboardingLink(gig.artistId, {
        returnUrl: here,
        refreshUrl: here,
      });
      window.location.assign(url);
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'Could not start Stripe onboarding.');
      setBusy(null);
    }
  }, [gig, payments]);

  const openDashboard = useCallback(async () => {
    if (!gig?.artistId) return;
    setBusy('dashboard');
    setNotice(null);
    try {
      const { url } = await payments.createDashboardLink(gig.artistId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'Could not open the Stripe dashboard.');
    } finally {
      setBusy(null);
    }
  }, [gig, payments]);

  const saveConfig = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setBusy('save');
      setNotice(null);
      setSaveError(null);
      // Convert dollar inputs to integer minor units at the edge (§22.5); drop blanks.
      const presets: { currency: string; amountMinor: number }[] = [];
      for (const raw of presetDollars) {
        const trimmed = raw.trim();
        if (!trimmed) continue;
        const minor = dollarsToMinor(Number(trimmed));
        if (minor === null || minor <= 0) {
          setNotice(`"${raw}" is not a valid tip amount.`);
          setBusy(null);
          return;
        }
        presets.push({ currency, amountMinor: minor });
      }
      const externalLinks: ExternalTipLink[] = links.map((d) => {
        const link: ExternalTipLink = { method: d.method, label: d.label.trim(), enabled: d.enabled };
        if (d.url.trim()) link.url = d.url.trim();
        if (d.instructions.trim()) link.instructions = d.instructions.trim();
        return link;
      });
      try {
        const saved = await payments.putTipConfig(gigId, {
          policy: {
            ...(heading.trim() ? { heading: heading.trim() } : {}),
            ...(thankYouText.trim() ? { thankYouText: thankYouText.trim() } : {}),
            presets,
            allowCustom,
          },
          externalLinks,
        });
        setHeading(saved.policy.heading ?? '');
        setThankYouText(saved.policy.thankYouText ?? '');
        setAllowCustom(saved.policy.allowCustom);
        setPresetDollars(saved.policy.presets.map((m) => formatMinor(m.amountMinor)));
        setLinks(saved.externalLinks.map(toDraft));
        setNotice('Tip settings saved.');
      } catch (err) {
        if (err instanceof ApiError) setSaveError(err);
        else setNotice('Could not save tip settings.');
      } finally {
        setBusy(null);
      }
    },
    [payments, gigId, presetDollars, currency, links, heading, thankYouText, allowCustom],
  );

  const currencyOptions = useMemo(() => {
    const set = new Set<string>(connection?.currencies ?? []);
    set.add(currency);
    return Array.from(set);
  }, [connection, currency]);

  if (loadError) {
    return (
      <main className="page">
        <p className="error">{loadError}</p>
        <Link className="link" to="/app">
          Back to overview
        </Link>
      </main>
    );
  }
  if (!gig) {
    return (
      <main className="page" aria-busy="true">
        <p>Loading tipping…</p>
      </main>
    );
  }

  return (
    <main className="page page--wide">
      <header className="app-bar">
        <div>
          <p className="eyebrow">
            <Link className="link" to="/app">
              Overview
            </Link>{' '}
            /{' '}
            <Link className="link" to={`/app/gigs/${gig.gigId}`}>
              {gig.name}
            </Link>{' '}
            / Tipping
          </p>
          <h1>Tipping</h1>
        </div>
      </header>

      <p className="help">
        Tips are appreciation for the performance. A tip never buys request priority or a guaranteed
        play — keep your copy and links consistent with that (§22.1).
      </p>

      {notice && <p className="muted">{notice}</p>}

      {/* ---------- (a) Connection ---------- */}
      <section className="card">
        <div className="module-head">
          <h2>Stripe connection</h2>
          <span className={`conn-badge conn-badge--${connection?.status ?? 'disconnected'}`}>
            {CONNECTION_LABEL[connection?.status ?? 'disconnected']}
          </span>
        </div>
        {!gig.artistId ? (
          <p className="muted">This gig has no artist, so no payout account can be connected.</p>
        ) : (
          <>
            <p className="help">
              Native (in-app) tipping stays off until Stripe reports the connected account is{' '}
              <strong>ready</strong> with the required capabilities (§22.3). Gigify stores only the
              connected-account reference — never Stripe keys or secrets.
            </p>
            <dl className="conn-meta">
              <div>
                <dt>Account reference</dt>
                <dd>{connection?.providerAccountRef ?? 'Not created yet'}</dd>
              </div>
              <div>
                <dt>Currencies</dt>
                <dd>
                  {connection && connection.currencies.length > 0
                    ? connection.currencies.join(', ')
                    : '—'}
                </dd>
              </div>
              <div>
                <dt>Capabilities</dt>
                <dd>
                  {connection && Object.keys(connection.capabilities).length > 0
                    ? Object.entries(connection.capabilities)
                        .map(([k, v]) => `${k}: ${v ? 'active' : 'inactive'}`)
                        .join(', ')
                    : '—'}
                </dd>
              </div>
            </dl>
            <p className={connReady ? 'muted' : 'hint'}>
              {connReady
                ? 'Native tipping is available for this account.'
                : 'Native tipping is OFF until onboarding is complete and capabilities are active.'}
            </p>
            <div className="button-row">
              <button
                type="button"
                className="action--primary"
                disabled={busy !== null}
                onClick={() => void startOnboarding()}
              >
                {connection && connection.status !== 'disconnected'
                  ? 'Continue Stripe onboarding'
                  : 'Connect Stripe'}
              </button>
              {connReady && (
                <button type="button" disabled={busy !== null} onClick={() => void openDashboard()}>
                  Stripe dashboard
                </button>
              )}
            </div>
          </>
        )}
      </section>

      {/* ---------- (b) Tip config editor ---------- */}
      <section className="card">
        <h2>Tip settings</h2>
        <p className="help">
          Configure what the audience sees on the public tip module (§22.4). Presets are entered in
          dollars and stored as exact minor units. One currency per gig.
        </p>
        <form className="tip-config" onSubmit={saveConfig}>
          <label>
            Heading
            <input
              value={heading}
              maxLength={80}
              placeholder="Enjoying the show?"
              onChange={(e) => setHeading(e.target.value)}
            />
          </label>
          <label>
            Thank-you text
            <textarea
              value={thankYouText}
              maxLength={280}
              rows={2}
              placeholder="Thanks for supporting live music!"
              onChange={(e) => setThankYouText(e.target.value)}
            />
          </label>

          <fieldset className="tip-presets">
            <legend>Preset amounts</legend>
            <label className="tip-presets__currency">
              Currency
              <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                {currencyOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <div className="tip-presets__rows">
              {presetDollars.map((val, i) => (
                <div className="tip-preset-row" key={i}>
                  <label className="visually-hidden" htmlFor={`preset-${i}`}>
                    Preset {i + 1} amount
                  </label>
                  <input
                    id={`preset-${i}`}
                    inputMode="decimal"
                    value={val}
                    placeholder="10.00"
                    onChange={(e) =>
                      setPresetDollars((p) => p.map((v, j) => (j === i ? e.target.value : v)))
                    }
                  />
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={`Remove preset ${i + 1}`}
                    onClick={() => setPresetDollars((p) => p.filter((_, j) => j !== i))}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="tip-presets__add"
              disabled={presetDollars.length >= 8}
              onClick={() => setPresetDollars((p) => [...p, ''])}
            >
              Add preset
            </button>
          </fieldset>

          <label className="checkbox">
            <input
              type="checkbox"
              checked={allowCustom}
              onChange={(e) => setAllowCustom(e.target.checked)}
            />
            Allow a custom amount
          </label>

          <fieldset className="tip-links">
            <legend>External tip links (§22.7)</legend>
            <p className="help">
              Shown only when enabled and valid. These record a click only — they never claim a
              payment succeeded, and no audience data is appended to the destination.
            </p>
            {links.map((d, i) => (
              <div className="tip-link-row" key={i}>
                <div className="tip-link-row__grid">
                  <label>
                    Method
                    <select
                      value={d.method}
                      onChange={(e) =>
                        setLinks((ls) =>
                          ls.map((l, j) =>
                            j === i
                              ? { ...l, method: e.target.value as ExternalTipLink['method'] }
                              : l,
                          ),
                        )
                      }
                    >
                      {TIP_LINK_METHODS.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Label
                    <input
                      value={d.label}
                      maxLength={60}
                      onChange={(e) =>
                        setLinks((ls) => ls.map((l, j) => (j === i ? { ...l, label: e.target.value } : l)))
                      }
                    />
                  </label>
                  {d.method === 'interac' ? (
                    <label className="tip-link-row__wide">
                      Interac instructions
                      <textarea
                        value={d.instructions}
                        maxLength={280}
                        rows={2}
                        onChange={(e) =>
                          setLinks((ls) =>
                            ls.map((l, j) => (j === i ? { ...l, instructions: e.target.value } : l)),
                          )
                        }
                      />
                    </label>
                  ) : (
                    <label className="tip-link-row__wide">
                      URL
                      <input
                        value={d.url}
                        inputMode="url"
                        placeholder="https://…"
                        onChange={(e) =>
                          setLinks((ls) => ls.map((l, j) => (j === i ? { ...l, url: e.target.value } : l)))
                        }
                      />
                    </label>
                  )}
                </div>
                <div className="tip-link-row__toggles">
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={d.enabled}
                      onChange={(e) =>
                        setLinks((ls) =>
                          ls.map((l, j) => (j === i ? { ...l, enabled: e.target.checked } : l)),
                        )
                      }
                    />
                    Enabled
                  </label>
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={`Remove link ${i + 1}`}
                    onClick={() => setLinks((ls) => ls.filter((_, j) => j !== i))}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              className="tip-links__add"
              onClick={() => setLinks((ls) => [...ls, emptyDraft()])}
            >
              Add external link
            </button>
          </fieldset>

          {saveError && (
            <div className="validation" role="alert">
              <p className="error">{saveError.message}</p>
              {saveError.fieldErrors.length > 0 && (
                <ul>
                  {saveError.fieldErrors.map((fe, i) => (
                    <li key={`${fe.path}-${i}`}>
                      <code>{fe.path}</code>: {fe.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="button-row">
            <button type="submit" className="action--primary" disabled={busy !== null}>
              {busy === 'save' ? 'Saving…' : 'Save tip settings'}
            </button>
          </div>
        </form>
      </section>

      {/* ---------- (c) Reporting ---------- */}
      <section className="card">
        <h2>Tip report</h2>
        <p className="help">
          Totals come from canonical payment state (§22.8). Pending or missing data is shown as
          "pending" / "—", never as zero.
        </p>
        <dl className="tip-summary">
          <div>
            <dt>Currency</dt>
            <dd>{summary?.currency ?? '—'}</dd>
          </div>
          <div>
            <dt>Gross successful</dt>
            <dd>
              {summary && summary.currency
                ? `${formatMinor(summary.grossMinor)} ${summary.currency}`
                : summary
                  ? 'pending'
                  : '—'}
            </dd>
          </div>
          <div>
            <dt>Successful count</dt>
            <dd>{summary ? summary.count : '—'}</dd>
          </div>
          <div>
            <dt>Refunds</dt>
            <dd>
              {summary && summary.currency
                ? `${formatMinor(summary.refundedMinor)} ${summary.currency}`
                : summary
                  ? 'pending'
                  : '—'}
            </dd>
          </div>
          <div>
            <dt>Disputes</dt>
            <dd>{summary ? summary.disputeCount : '—'}</dd>
          </div>
        </dl>

        {tips.length === 0 ? (
          <p className="muted">No tips recorded yet.</p>
        ) : (
          <div className="tips-scroll">
            <table className="tips-table">
              <thead>
                <tr>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Message</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {tips.map((t) => (
                  <tr key={t.tipId}>
                    <td>
                      {t.amountMinor > 0 ? `${formatMinor(t.amountMinor)} ${t.currency}` : '—'}
                    </td>
                    <td>
                      <span className={`tip-status tip-status--${t.status as TipStatus}`}>
                        {t.status}
                      </span>
                    </td>
                    <td>{t.message ?? '—'}</td>
                    <td>{fmtTime(t.succeededAt ?? t.createdAt ?? null)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}
